import React, { useRef } from 'react';
import { Flex, Divider } from '@televet/kibble-ui/build/chakra';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { EncryptedSignedUploadPurpose, useEncryptAndUploadDataMutation } from 'shared/types/graphql';
import chunk from 'lodash-es/chunk';
import partial from 'lodash-es/partial';

const UploadClinicDataForm = (): JSX.Element => {
  const clinicFileInputRef = useRef<HTMLInputElement>(null);
  const repFileInputRef = useRef<HTMLInputElement>(null);
  const [encryptAndUploadData] = useEncryptAndUploadDataMutation();
  const toast = useToast();

  const handleFileUpload =
    (purpose: EncryptedSignedUploadPurpose) =>
    async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      const file = e.target.files?.[0];

      // Reset the input so that the user can upload different files
      e.target.files = null;

      if (!file) {
        return;
      }

      let body = '';

      if (file.type === 'text/csv') {
        body = btoa(await file.text());
      } else {
        const rawData: number[] = new Uint8Array(await file.arrayBuffer()) as unknown as number[];
        const binary = chunk(rawData, 32000)
          .map((bytesChunk) => String.fromCharCode.apply(null, bytesChunk))
          .join('');
        body = btoa(binary);
      }

      const { data } = await encryptAndUploadData({
        variables: {
          body,
          purpose,
          extension: file.name.split('.').pop() ?? file.type.split('/').pop() ?? '',
        },
      });

      if (!data?.encryptAndUploadData.success) {
        toast({
          title: 'File upload failure!',
          description: 'An error has occurred. Our team has been notified, but please try again.',
          status: 'error',
        });
        throw new Error('Upload Failed');
      }

      toast({
        title: 'File uploaded!',
        description: 'We will notify you when the file has been processed.',
        status: 'success',
      });
    };

  const selectClinicFile = (): void => {
    clinicFileInputRef.current?.click();
  };

  const selectRepFile = (): void => {
    repFileInputRef.current?.click();
  };

  return (
    <>
      <Divider mb={5} />
      <Flex direction="column" gap={3} mb={5} maxWidth="445px">
        <Heading size="md" mb={1}>
          Upload Information
        </Heading>
        <Heading size="sm">Upload Information for Clinics</Heading>
        <Text size="sm" variant="subtle">
          You may upload information for any clinic you have access to. Please upload a CSV or XLSX file using the
          provided template.
        </Text>
        <TextInput
          type="file"
          accept=".csv, .xlsx"
          onChange={partial(handleFileUpload(EncryptedSignedUploadPurpose.LegalEntityClinic))}
          ref={clinicFileInputRef}
          display="none"
        />
        <Button size="sm" variant="secondary" w="240px" onClick={selectClinicFile}>
          Select file
        </Button>
        <Heading mt={4} size="sm">
          Upload Information for Legal Representative
        </Heading>
        <Text size="sm" variant="subtle">
          You may upload information to use for the legal representative for any clinic you have access to. Please
          upload a CSV or XLSX file using the provided template. You may also upload a photo ID for the legal
          representative, providing both a front and back image.
        </Text>
        <TextInput
          type="file"
          accept=".csv, .xlsx, .png, .jpg, .jpeg, .heic"
          onChange={partial(handleFileUpload(EncryptedSignedUploadPurpose.LegalEntityRepresentative))}
          ref={repFileInputRef}
          display="none"
        />
        <Button size="sm" variant="secondary" w="240px" onClick={selectRepFile}>
          Select file
        </Button>
      </Flex>
      <Divider mb={5} />
    </>
  );
};

export default UploadClinicDataForm;
